import { Link } from '@tanstack/react-router'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { isIncompleteSalesReport } from '@/lib/services/money/money.query'

export function SalesReportError({ error, orgId, onRetry, isRetrying, hasCachedReport = false }) {
  const path = usePathHelper()
  const incomplete = isIncompleteSalesReport(error)
  const issues = incomplete ? (error.response.data.issues ?? []) : []
  return (
    <InfoNotice
      tone="warning"
      title={
        incomplete
          ? 'An order needs attention'
          : hasCachedReport
            ? 'Sales couldn’t be refreshed'
            : 'Sales couldn’t be loaded'
      }
      action={
        onRetry && (
          <Button variant="notice" size="sm" onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? 'Checking…' : 'Try again'}
          </Button>
        )
      }
      body={
        incomplete && (
          <div className="space-y-3">
            {issues.map((issue, index) => (
              <div key={index}>
                <Typography variant="labelStrong">
                  {issue.source === 'fsn_network'
                    ? `FSN order ${issue.external_reference}`
                    : issue.source === 'condolences'
                      ? `Condolences order ${issue.external_reference}`
                      : `Order #${issue.sales_order_id}`}
                </Typography>
                <Typography variant="description">{issue.message}</Typography>
                {issue.sales_order_id && (
                  <Button asChild variant="link">
                    <Link {...path.salesOrders.show(issue.sales_order_id).using({ orgId }).link}>
                      View order #{issue.sales_order_id}
                    </Link>
                  </Button>
                )}
              </div>
            ))}
            <Typography variant="description">
              Contact support with {issues.length ? 'this order reference' : 'the selected dates'}{' '}
              to correct the missing details. You can still view other date ranges.
            </Typography>
          </div>
        )
      }
    >
      {incomplete
        ? 'Some order details are missing for these dates. Sales totals are hidden until they can be verified.'
        : hasCachedReport
          ? 'Showing the last loaded report. Try again to check for updates.'
          : 'Try again in a moment. If this keeps happening, contact support.'}
    </InfoNotice>
  )
}
